import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useAtom } from 'jotai';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next'

import { fetch_one, fetch_all } from '../../lib/server_helper';

import {
  Divider,
  Grid
} from '@mui/material';

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

import { loginAtom } from '../../lib/auth';

import { AlertCheckInputs } from '../../components/alerts/CheckInputs';
import { AlertSaved } from '../../components/alerts/Saved';
import { AlertSomethingWentWrong } from '../../components/alerts/SomethingWentWrong';

import {
  Dt,
  LanguageFab,
  Page,
  StyledButton,
  StyledColourPicker,
  StyledSwitch,
  StyledTextField,
  StyledTextFieldTransl,
  Typography
} from '../../components/interface';

import { Location } from '../../models';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchOne } from '../../lib/v31lib';

// const { DateTime } = require("luxon");

type AvailableLang = 'nl' | 'fr' | 'en';
type Props = {};

export const AdminLocationDetail: FunctionComponent<Props> = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [login, setLogin] = useAtom(loginAtom);
  const {t, i18n} = useTranslation(['translations']);

  const [activeLocale, setActiveLocale] = useState<AvailableLang>('en');

  const params = useParams();
  const memoqSettingId: string | undefined = params.memoq_setting_id;
  const [objectId, setObjectId] = useState<string | undefined>(params.id);
  const [mainObject, setMainObject] = useState<Location>({
    name: {
      en: '',
      nl: '',
      fr: ''
    }
  });
  const queryClient = useQueryClient();

  const [savedOpen, setSavedOpen] = useState<boolean>(false);
  const [errorSnackOpen, setErrorSnackOpen] = useState<boolean>(false);
  const [serverErrorSnackOpen, setServerErrorSnackOpen] = useState<boolean>(false);
  const [exitSavedSnackOpen, setDividendSavedSnackOpen] = useState<boolean>(false);


  const {
    data: object,
    isLoading: objectLoading,
    isError: objectError,
    isSuccess: objectSuccess,
    refetch: objectRefetch,
  } = useQuery({
    queryKey: ["memoq_location", objectId],
    queryFn: () =>
      fetchOne<Location>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'location',
          id: objectId,
          fields: [
            'id', 'name', 'memoq_setting_id', 'extra_data', 'current_nof_children', 'current_nof_workers'
          ]
        },
        login
      ),
    enabled: parseInt(objectId || '', 10) > 0,
  });









  const mutationSave = useMutation({
    mutationFn: (pars: {
      formData: any;
      keepOpen?: boolean
    }) => {
      return fetchOne<Location>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/save`,
        pars.formData,
        login
      );
    },
    onMutate: (pars: {
      formData: any;
    }) => {
      return true;
    },
    onError: (data, variables, context) => {
      return true;
    },
    onSuccess: (data, variables, context) => {
      setSavedOpen(true);
      queryClient.invalidateQueries({ queryKey: ["memoq_locations"] });
      queryClient.invalidateQueries({ queryKey: ["memoq_location", objectId] });
      navigate(`/locations/${memoqSettingId}`);
    },
  });













  useEffect(() => {
    if (params.id !== 'new') objectRefetch();
  }, [params.id]);
  useEffect(() => {
    if (!!objectSuccess) setMainObject({
      ...object
    });
  }, [object]);

  return (
    <Page sx={{}} title={`${t("objects.memoq_location.title")} (${activeLocale})`}>
      <LanguageFab
        activeLocale={activeLocale}
        setActiveLocale={(lc) => {setActiveLocale(lc);}}
      />
      <Grid container spacing="8">

        <Grid item container xs={12} spacing="4" sx={{alignContent: 'baseline'}}>
          <Grid item xs={12}>
            <StyledTextFieldTransl
              label="Name"
              activeLocale={activeLocale}
              value={mainObject?.name || {en: ''}}
              id="name"
              onChange={(v) => {
                setMainObject({
                  ...(mainObject || {}),
                  name: v
                });
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <StyledTextField
              label="# children"
              value={mainObject?.current_nof_children?.toString() || '0'}
              id="current_nof_children"
              onChange={(v) => {
                setMainObject({
                  ...(mainObject || {}),
                  current_nof_children: !!v ? parseInt(v, 10) : undefined
                });
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <StyledTextField
              label="# workers"
              value={mainObject?.current_nof_workers?.toString() || '0'}
              id="current_nof_workers"
              onChange={(v) => {
                setMainObject({
                  ...(mainObject || {}),
                  current_nof_workers: !!v ? parseInt(v, 10) : undefined
                });
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <Typography sx={{textDecoration: 'underline'}}>Children history</Typography>
            {(mainObject?.extra_data?.children_history || []).map(hist => <Typography><Dt d={hist[0]} f="dd/LL/y" />&nbsp;&nbsp;&nbsp;&nbsp;{hist[1]} =&gt; {hist[2]}</Typography>)}
          </Grid>
          <Grid item xs={6}>
            <Typography sx={{textDecoration: 'underline'}}>Worker history</Typography>
            {(mainObject?.extra_data?.worker_history || []).map(hist => <Typography><Dt d={hist[0]} f="dd/LL/y" />&nbsp;&nbsp;&nbsp;&nbsp;{hist[1]} =&gt; {hist[2]}</Typography>)}
          </Grid>
        </Grid>





        <Grid item xs={12} sx={{alignContent: 'baseline', textAlign: 'center', position: 'sticky', bottom: 0, backgroundColor: 'white', zIndex: 1201}}>
          <Divider sx={{marginTop: 1, marginBottom: 2}} />

          <StyledButton
            label={t("public.buttons.cancel")}
            disabled={objectLoading || mutationSave.isPending}
            id='cancel'
            // contained
            onClick={(v) => {
              navigate(`/locations/${memoqSettingId}`);
            }}
            sx={{marginLeft: 1}}
          />
          <StyledButton
            label={t("public.buttons.save")}
            id='save'
            disabled={objectLoading || mutationSave.isPending || !mainObject.name}
            contained
            onClick={(v) => {
              mutationSave.mutate({
                formData: {
                  object: 'location',
                  handler_id: login?.id,
                  fields: ['id'],
                  id: mainObject?.id,
                  ob: {
                    ...mainObject,
                    memoq_setting_id: memoqSettingId
                  }
                }
              });
            }}
            sx={{marginLeft: 1}}
          />
        </Grid>
      </Grid>

      {!!serverErrorSnackOpen && <AlertSomethingWentWrong open={serverErrorSnackOpen} setOpen={(b) => {setServerErrorSnackOpen(b);}} />}
      {!!errorSnackOpen && <AlertCheckInputs open={errorSnackOpen} setOpen={(b) => {setErrorSnackOpen(b);}} />}
      {!!savedOpen && <AlertSaved open={savedOpen} setOpen={(b) => {setSavedOpen(b);}} />}
    </Page>
  );
}
